import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { toast } from 'vue-sonner';
import { useSavedViewQuery } from '@/composables/api/useSavedViewsApi';
import { useSavedViews } from '@/composables/useSavedViews';
import { useRecentItemsStore } from '@/stores/recentItemsStore';
import { usePersistentTableStore } from '@/stores/persistentTableStore';
import { viewPathFor } from '@/navigation/areas';
import type { SavedViewRecord } from '@/utils/savedViews';
import { decideRoutedView, parseViewParam, type ViewLoad } from '@/composables/routedSavedView';

/**
 * Bumped when the user opens the view that is already in the address, for
 * example by clicking its sidebar link again. The address does not change, so
 * the router does nothing. The list puts the saved view back instead, which
 * throws away unsaved changes the way picking the view always has.
 */
const reopenRequests = ref(0);

export function reopenOpenView() {
    reopenRequests.value += 1;
}

export interface RoutedSavedViewOptions {
    /**
     * Applies a view the address names. Track matching passes its own, so its
     * unsaved-changes guard can ask before a view replaces pending edits.
     * Without one the view is applied straight away.
     */
    applyView?: (view: SavedViewRecord) => void;
}

/**
 * Wires a list, or a page with saved views such as Track matching, to
 * `?view=<id>`: opening `/players?view=12` applies view 12, and picking or
 * clearing a view updates the address. The rules are in routedSavedView.ts.
 */
export function useRoutedSavedView(options: RoutedSavedViewOptions = {}) {
    const route = useRoute();
    const router = useRouter();
    // The scope the saved views are stored under: the list's entity, or a page's
    // own id such as `TrackMatching`.
    const tableStore = usePersistentTableStore();
    const { activeView, handlers } = useSavedViews();
    const applyView = options.applyView ?? ((view: SavedViewRecord) => handlers.onSelectView(view));
    const recentItems = useRecentItemsStore();

    const queryViewId = computed(() => parseViewParam(route.query.view));
    const activeViewId = computed(() => activeView.value?.id ?? null);

    const viewQuery = useSavedViewQuery(queryViewId, () => queryViewId.value !== null && queryViewId.value !== activeViewId.value);
    const load = computed<ViewLoad>(() => {
        if (viewQuery.isLoading.value || viewQuery.isFetching.value) return { state: 'loading' };
        const view = viewQuery.data.value;
        return view ? { state: 'loaded', view } : { state: 'missing' };
    });

    function withoutView() {
        const rest = { ...route.query };
        delete rest.view;
        return rest;
    }

    function run(addressChanged: boolean) {
        const action = decideRoutedView({
            entityId: tableStore.storeKey,
            queryViewId: queryViewId.value,
            activeViewId: activeViewId.value,
            load: load.value,
            addressChanged,
        });
        switch (action.kind) {
            case 'apply':
                applyView(action.view);
                return;
            case 'redirect': {
                // A view of another list or page goes there. A view that belongs to
                // no area has nowhere to go.
                const destination = viewPathFor(action.entityId, action.viewId);
                if (destination) {
                    router.replace(destination);
                } else {
                    toast.error("That saved view can't be opened here.");
                    router.replace({ query: withoutView() });
                }
                return;
            }
            case 'drop-missing':
                toast.error("That saved view doesn't exist, or isn't yours.");
                router.replace({ query: withoutView() });
                return;
            case 'clear-query':
                router.replace({ query: withoutView() });
                return;
            case 'set-query':
                router.replace({ query: { ...route.query, view: String(action.viewId) } });
                return;
        }
    }

    // The address changed, or the view it names finished loading.
    watch([queryViewId, () => load.value.state], () => run(true), { immediate: true });
    // The user picked or cleared a view without going through the address.
    watch(activeViewId, () => run(false));
    // Opened views join Home's and Cmd+K's recent items, beside opened records.
    // This watches for the address and the open view agreeing, not for a view
    // being applied: after a reload, or right after saving a new view, the view
    // is already open and is never applied, and it still counts as opened.
    const openedViewId = computed(() =>
        activeView.value && activeView.value.id === queryViewId.value ? activeView.value.id : null,
    );
    watch(
        openedViewId,
        (viewId) => {
            const view = activeView.value;
            if (viewId === null || !view) return;
            recentItems.remember({ kind: 'view', entityId: view.entity, id: view.id, label: view.name });
        },
        { immediate: true },
    );
    watch(reopenRequests, () => {
        if (activeView.value && activeView.value.id === queryViewId.value) {
            applyView(activeView.value);
        }
    });

    return { activeView };
}
